import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery } from 'react-query';
import * as yup from 'yup';
import { useFormik } from 'formik';
import api from '../../../../components/api';
import { yupErrorMsg } from '../../../../components/atoms/const';
import { sorter } from '../../../../components/atoms/func';

export type VMConfigRes = {
  id: number,
  name: null,
  extensions: null,
  vmTrays: {
    id: {
      configurationId: null,
      trayId: null
    },
    tray: {
      id: number,
      start: string,
      numbering: number,
      selection: number,
      columnCodes: string[],
      productCategories: never[]
    },
    sequence: number
  }[],
  columnMapping: null
}[] | []

function useAddVendingMachine() {
  const isCoffee: boolean = React.useMemo(
    () => window.location.pathname.split('/')[1] === 'coffee-machine',
    [window.location.pathname],
  );

  const isWholesale: boolean = React.useMemo(
    () => window.location.pathname.split('/')[1] === 'wholesale',
    [window.location.pathname],
  );

  /* Navigation Handler */
  const navigate = useNavigate();
  const navigateToVM = () => {
    if (!isCoffee && !isWholesale) {
      navigate('/vending-machine');
    } else if (!isWholesale) {
      navigate('/coffee');
    } else {
      navigate('/wholesale');
    }
  };

  /* Url format: vending-machine/add/:clientName/:vmId
  * and used when you are on edit mode */
  const { clientName, vmId } = useParams();

  /* Fetch data from a Real-Time Database using react-query to get modelList
  * and then manage it with function to make sure it will changed when API return
  * the right data */
  const { data: vmModelListData, isLoading: vmModelListLoading } = useQuery(
    ['getAllModel', 'vendingMachine'],
    () => api.vendingMachine.getAllModel(),
    { enabled: !isCoffee && !isWholesale },
  );
  const { data: coffeeModelData, isLoading: coffeeModelLoad } = useQuery(
    ['getAllModel', 'coffeeMachine'],
    () => api.coffeeMachine.getAllModel(),
    { enabled: isCoffee || isWholesale },
  );
  const vmModelList = React.useMemo(() => {
    console.log('Loading Model...');
    const vmData = vmModelListData?.data?.data;
    const coffeeData = coffeeModelData?.data?.data;
    if (vmData && !isCoffee && !isWholesale) {
      return vmData.filter((data) => data.numberOfConfigurations > 0)
        .map((data) => ({
          label: data.model,
          value: data.id.toString(),
        }));
    }
    if (coffeeData && (isCoffee || isWholesale)) {
      console.log('coffee models', coffeeData);
      return coffeeData.map((data) => ({
        label: data.replaceAll('_', ' '),
        value: data,
      }));
    }
    return [];
  }, [vmModelListData, coffeeModelData, isCoffee, isWholesale]);

  /* Same with the function before, but it for handling clientList Data */
  const { data: clientListData, isLoading: clientListLoading } = useQuery(
    ['getAllActive', 'clients'],
    () => api.clients.getAllActive(),
  );
  const clientList = React.useMemo(() => {
    const resData = clientListData?.data?.data;
    if (resData) {
      return resData.map((data: any) => ({
        label: data.name,
        value: data.id,
      }));
    }
    return undefined;
  }, [clientListData]);

  /* function for handling add Vending Machine to Database also with form validation */
  const { isLoading: addVMLoad, mutate: addVM } = useMutation(api.vendingMachine.add, {
    onMutate: (x) => console.log('onMutate', x),
    onSuccess: (res) => {
      alert('success');
      console.log('add result vm', res);
      navigateToVM();
    },
    onError: (err: Error) => {
      alert('error');
      console.log('err', err.message);
    },
  });
  const { isLoading: addCoffeeLoad, mutate: addCoffee } = useMutation(api.coffeeMachine.add, {
    onMutate: (x) => console.log('onMutate', x),
    onSuccess: (res) => {
      alert('success');
      console.log('add result coffee', res);
      navigateToVM();
    },
    onError: (err: Error) => {
      alert('error');
      console.log('err', err.message);
    },
  });
  // Form validation
  const yupSchema = !isCoffee && !isWholesale ? yup.object({
    clientId: yup.string().required(yupErrorMsg.CLIENT_EMPTY),
    location: yup.string().required(yupErrorMsg.LOCATION_EMPTY),
    model: yup.string().required(yupErrorMsg.MODEL_EMPTY),
    modelConfigId: yup.string().required(yupErrorMsg.MACHINE_CONFIG_EMPTY),
  }) : yup.object({
    clientId: yup.string().required(yupErrorMsg.CLIENT_EMPTY),
    location: yup.string().required(yupErrorMsg.LOCATION_EMPTY),
    model: yup.string().required(yupErrorMsg.MODEL_EMPTY),
  });
  const formik = useFormik({
    initialValues: {
      deviceId: '',
      clientId: '',
      location: '',
      model: '',
      extModelConfigId: 0,
      modelConfigId: '',
    },
    validationSchema: yupSchema,
    onSubmit: (values) => {
      if (!isCoffee && !isWholesale) {
        return (
          addVM({
            deviceId: values.deviceId,
            clientId: values.clientId,
            location: values.location,
            extModelConfigId: 0,
            modelConfigId: values.modelConfigId,
          })
        );
      }
      return (
        addCoffee({
          deviceId: values.deviceId,
          model: values.model,
          clientId: values.clientId,
          location: values.location,
        })
      );
    },
  });

  /* Get Vending Machine By Id from API handler react-select
  * only will run when on edit mode and have vmId from url */
  const { data: vmById } = useQuery(
    ['getById', 'vendingMachines', vmId],
    () => api.vendingMachine.getById(vmId!),
    {
      enabled: !!vmId,
    },
  );

  /* Function that are only running when on edit mode and will
  * auto-fill form from real-time database
  *  */
  React.useEffect(() => {
    const resData = vmById?.data?.data;

    // Triggered when url only have clientId when edit client
    if (clientName && clientList && vmModelList) {
      const findClientByName = clientList.find((client : any) => client.label.toString().toLowerCase() === clientName.replaceAll('-', ' ').toLowerCase());
      if (findClientByName) formik.setFieldValue('clientId', findClientByName.value);

      // Triggered when url have vmId and clientId when edit vm detail
      if (vmId && resData) {
        formik.setFieldValue('deviceId', resData.deviceId);
        formik.setFieldValue('location', resData.location);

        if (findClientByName) formik.setFieldValue('clientId', findClientByName.value);

        const findConfigByName = vmModelList?.find((model : any) => model.label.toString().toLowerCase() === resData?.model?.toLowerCase());
        if (findConfigByName)formik.setFieldValue('modelId', findConfigByName.value);
      }
    }
  }, [vmById, vmId, clientList, vmModelList, clientName]);

  /* VM Config
  * This function will only used when user already choose their modelId and
  * triggered by formik values modelId changed. Changing modelId will make
  * currentVMConfig back to 0, its needed because all vmConfig at vmList have
  * minimum count 1
  * */

  // state management to handle current vm config that are being viewed
  const [currentVmConfig, setCurrentVmConfig] = React.useState<number>(0);

  // fetch method by react-query
  const { data: vmConfigListRes, mutate: mutateVmConfig, isLoading: vmConfigLoading } = useMutation(api.vendingMachine.getConfigByModel, {
    onMutate: (x) => console.log('onMutate', x),
    onSuccess: (res) => console.log('vmConfigList', res),
    onError: (err: Error) => console.log('errVmConfigList', err.message),
  });

  // re-fetch new vm config list when modelId changed
  React.useEffect(() => {
    if (formik.values.model) {
      formik.setFieldTouched('modelConfigId', false);
      setCurrentVmConfig(0);
      const findConfigByName = vmModelList?.find((model : any) => model.value === formik.values.model);
      if (findConfigByName) mutateVmConfig(findConfigByName.label);
    }
  }, [formik.values.model]);

  // Result handler from fetching data in API, example data like dummyConfig.json
  const vmConfigList : VMConfigRes = React.useMemo(() => vmConfigListRes?.data?.data ?? [], [vmConfigListRes]);

  // state management for current viewed vm config by user
  const onPreviousVmConfig = () => setCurrentVmConfig((currentVmConfig + vmConfigList.length - 1) % vmConfigList.length);
  const onNextVmConfig = () => setCurrentVmConfig((currentVmConfig + 1) % vmConfigList.length);

  const sortedVM = React.useMemo(() => {
    if (vmConfigList && vmConfigList.length && vmConfigList[currentVmConfig].vmTrays) {
      return vmConfigList[currentVmConfig].vmTrays.map((vm) => ({
        start: vm.tray.start,
        columnCodes: vm.tray.columnCodes,
        id: vm.tray.id,
      })).sort((a, b) => sorter(a.start, b.start));
    }
    return [];
  }, [vmConfigList, currentVmConfig]);

  // Function for button that are handling current chosen Vending Machine Configuration
  const handleChooseConfig = () => {
    formik.setFieldValue('modelConfigId', vmConfigList[currentVmConfig].id);
  };
  // Button style would be different depend it selected or not
  const handleCurrentConfig:boolean = React.useMemo(() => {
    if (vmConfigList && vmConfigList[currentVmConfig]) {
      return vmConfigList[currentVmConfig].id.toString() === formik.values.modelConfigId.toString();
    }
    return false;
  }, [currentVmConfig, formik.values.modelConfigId]);

  return {
    isCoffee,
    isWholesale,
    clientName,
    vmId,
    vmModelList,
    vmModelListLoading,
    clientList,
    clientListLoading,
    formik,
    addVMLoad,
    addCoffeeLoad,
    vmConfigLoading,
    currentVmConfig,
    coffeeModelLoad,
    onPreviousVmConfig,
    onNextVmConfig,
    sortedVM,
    vmConfigList,
    handleChooseConfig,
    handleCurrentConfig,
    navigateToVM,
  };
}

export default useAddVendingMachine;
